/**
 * When a past meeting happened — a fuller label than `activityTime`, which is the wrong formatter
 * here despite the two lists sitting one under the other.
 *
 * An activity row is a thing that just happened, so `activityTime` shows a bare clock for today and
 * drops the time entirely past a week ("May 3"). A history is looked things up in: "May 3" cannot be
 * told apart from the other meeting on May 3, and "9:03 AM" leaves the reader to work out the day.
 * Every row here carries a date AND a time.
 */

/** "Today" and "Yesterday", already translated by the caller. */
export interface MeetingTimeLabels {
  today: string;
  yesterday: string;
}

/** Midnight at the start of the day `at` falls in, in the runtime's own zone. */
function startOfDay(at: number): Date {
  const d = new Date(at);
  d.setHours(0, 0, 0, 0);
  return d;
}

/** `days` whole days before `from`, by calendar rather than by arithmetic — 86_400_000 ms is not a
 *  day across a DST boundary, and an hour of drift there moves a row into the wrong bucket. */
function daysBefore(from: Date, days: number): Date {
  const d = new Date(from);
  d.setDate(d.getDate() - days);
  return d;
}

/**
 * The meeting's own span: "Sat, Sep 13 · 7:35 PM–8:23 PM", or just "Sat, Sep 13 · 7:35 PM" when the end
 * is unknown. Each end is formatted on its own, with its own day-period — deliberately not
 * `formatRange`'s shared "7:35 – 8:23 PM", because both phones render this exact string and the three
 * surfaces must agree on it. Empty string when the start is absent or unparseable.
 *
 * The times are the MEETING's, from the transcript's first and last stamps — never the write-up's.
 * Rendered in the reader's own zone by `toLocaleTimeString`, which is the only zone a browser has.
 */
export function meetingTimeRange(
  startedAt: number | undefined,
  endedAt: number | undefined,
  now: number,
  locale: string | undefined,
  labels: MeetingTimeLabels,
): string {
  // The marker's reader (common-lib `readMeetingNotesCard`) keeps any FINITE stamp, so the guard against
  // a 0 or negative one — which would draw "Jan 1, 1970" — lives here, at render time. Shared rule with
  // both phones' date formatters: no positive start, no range.
  if (!isStamp(startedAt)) return '';
  const start = new Date(startedAt);
  const day = meetingDay(startedAt, now, locale, labels);
  if (!isStamp(endedAt) || endedAt <= startedAt) return `${day} · ${clock(start, locale)}`;
  return `${day} · ${clock(start, locale)}–${clock(new Date(endedAt), locale)}`;
}

/**
 * How long the meeting ran, in whole hours and minutes, rounded to the nearest minute and never below
 * one — a meeting the agent sat in for forty seconds still ran. Null when either end is missing or the
 * span is not positive, so a caller draws nothing rather than "0 min".
 */
export function meetingDuration(startedAt: number | undefined, endedAt: number | undefined): { hours: number; minutes: number } | null {
  if (!isStamp(startedAt) || !isStamp(endedAt)) return null;
  const span = endedAt - startedAt;
  if (span <= 0) return null;
  const totalMinutes = Math.max(1, Math.round(span / 60_000));
  return { hours: Math.floor(totalMinutes / 60), minutes: totalMinutes % 60 };
}

/** A usable epoch-ms stamp: finite and after the epoch. */
function isStamp(value: number | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

/**
 * The stamp when it is usable, else undefined — for a caller that wants to FALL BACK (`meetingStamp(a) ??
 * b`) rather than draw nothing. A 0 or negative value would otherwise format as Jan 1, 1970.
 */
export function meetingStamp(value: number | undefined): number | undefined {
  return isStamp(value) ? value : undefined;
}

function clock(d: Date, locale: string | undefined): string {
  return d.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' });
}

/** "Today", "Yesterday", "Tue, Sep 16", "Sep 3", "Sep 3, 2025" — the day half of every label here. */
function meetingDay(then: number, now: number, locale: string | undefined, labels: MeetingTimeLabels): string {
  const d = new Date(then);
  const today = startOfDay(now);

  if (then >= today.getTime()) return labels.today;
  if (then >= daysBefore(today, 1).getTime()) return labels.yesterday;

  // Within the last week the weekday is how people remember a meeting, but it is ambiguous on its
  // own — this Tuesday and last Tuesday read identically — so it comes with its date.
  if (then >= daysBefore(today, 6).getTime()) {
    return d.toLocaleDateString(locale, { weekday: 'short', month: 'short', day: 'numeric' });
  }

  const sameYear = d.getFullYear() === new Date(now).getFullYear();
  return d.toLocaleDateString(locale, sameYear ? { month: 'short', day: 'numeric' } : { year: 'numeric', month: 'short', day: 'numeric' });
}

/**
 * A day group's heading: "Today · Sat, Sep 26", "Tomorrow · Sun, Sep 27", "Yesterday · Fri, Sep 25", and
 * otherwise "Monday · Sep 28" (with the year once it is not this year's). The relative word comes from
 * `Intl.RelativeTimeFormat`, so it is in the reader's language with no copy of our own.
 */
export function meetingDayHeading(day: number, now: number, locale: string | undefined): string {
  const d = new Date(day);
  const days = Math.round((startOfDay(day).getTime() - startOfDay(now).getTime()) / 86_400_000);
  if (Math.abs(days) <= 1) {
    const word = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(days, 'day');
    const date = d.toLocaleDateString(locale, { weekday: 'short', month: 'short', day: 'numeric' });
    return `${word.charAt(0).toLocaleUpperCase(locale) + word.slice(1)} · ${date}`;
  }
  const sameYear = d.getFullYear() === new Date(now).getFullYear();
  const weekday = d.toLocaleDateString(locale, { weekday: 'long' });
  const date = d.toLocaleDateString(locale, sameYear ? { month: 'short', day: 'numeric' } : { year: 'numeric', month: 'short', day: 'numeric' });
  return `${weekday.charAt(0).toLocaleUpperCase(locale) + weekday.slice(1)} · ${date}`;
}

/** A meeting's start as a bare clock, "10:30" or "10:30 AM" by locale — the time column of a day group. */
export function meetingClock(at: number, locale: string | undefined): string {
  return clock(new Date(at), locale);
}
